import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { List, Tag, Typography, Button, Space, Empty, Skeleton, Segmented, Select, message } from 'antd';
import { ReloadOutlined, RightOutlined, HomeOutlined, UserAddOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { fetchLocationSummary, fetchRoster, setLocationAssignment, type LocationSummary, type RosterMember } from '../config/api';
import { shortActor } from '../components/triageMaps';
import { useCurrentUser } from '../hooks/useCurrentUser';

dayjs.extend(relativeTime);

const { Text, Title } = Typography;
const POLL_MS = 60_000;

// Ops works per PROPERTY — schedule a visit, confirm with the hotel, then work
// its queue — so the worklist ranks LOCATIONS by how much actionable work each
// holds (most cleared per trip) and lets one ops member own each property.
// Assignment is reassignable by anyone; the roster is recent triage/claim actors.
const WorklistPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const me = user?.email ?? user?.sub ?? null;
  const myName = user?.name || (me ? shortActor(me) : 'you');
  const [messageApi, contextHolder] = message.useMessage();

  const [locations, setLocations] = useState<LocationSummary[]>([]);
  const [roster, setRoster] = useState<RosterMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState<'mine' | 'unassigned' | 'all'>('all');
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [locs, ros] = await Promise.all([
        fetchLocationSummary(),
        fetchRoster().catch(() => [] as RosterMember[]),
      ]);
      setLocations(locs);
      setRoster(ros);
    } catch (err: any) {
      if (!silent) messageApi.error(`Failed to load worklist: ${err.message}`);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => void load(true), POLL_MS);
    const onFocus = () => void load(true);
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [load]);

  const assign = useCallback(async (locationId: string, assignedTo: string | null) => {
    setSavingId(locationId);
    // Optimistic — reconcile on error.
    setLocations((prev) => prev.map((l) => (l.location_id === locationId
      ? { ...l, assigned_to: assignedTo, assigned_by: me, assigned_at: new Date().toISOString() }
      : l)));
    try {
      await setLocationAssignment(locationId, assignedTo);
      const name = assignedTo === me ? 'you' : (roster.find((r) => r.email === assignedTo)?.name || shortActor(assignedTo));
      messageApi.success(assignedTo ? `Assigned to ${name}` : 'Unassigned');
    } catch (err: any) {
      messageApi.error(`Couldn't update assignment: ${err.message}`);
      void load(true);
    } finally {
      setSavingId(null);
    }
  }, [me, messageApi, load, roster]);

  const visible = locations.filter((l) => {
    if (scope === 'mine') return me != null && l.assigned_to === me;
    if (scope === 'unassigned') return l.assigned_to == null;
    return true;
  });

  // Assignable options: me first, then recent actors (deduped).
  const rosterOptions = (() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    if (me) { opts.push({ value: me, label: `${myName} (you)` }); seen.add(me); }
    for (const a of roster) { if (!seen.has(a.email)) { opts.push({ value: a.email, label: a.name }); seen.add(a.email); } }
    return opts;
  })();

  const mineCount = me ? locations.filter((l) => l.assigned_to === me).length : 0;
  const unassignedCount = locations.filter((l) => l.assigned_to == null).length;

  return (
    <div style={{ height: '100%', overflow: 'auto', background: '#f0f2f5' }}>
      {contextHolder}
      <div style={{ maxWidth: 920, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <Title level={4} style={{ margin: 0 }}>Worklist — properties by workload</Title>
          <Button size="small" icon={<ReloadOutlined />} onClick={() => load()} loading={loading}>Refresh</Button>
        </div>
        <Text type="secondary">
          Properties ranked by how much actionable triage each holds — schedule the
          visit that clears the most per trip. Assign a property to whoever owns
          its hotel relationship, then open its queue.
        </Text>

        <div style={{ margin: '16px 0' }}>
          <Segmented<'mine' | 'unassigned' | 'all'>
            size="small"
            value={scope}
            onChange={setScope}
            options={[
              { label: `All (${locations.length})`, value: 'all' },
              { label: `Mine (${mineCount})`, value: 'mine' },
              { label: `Unassigned (${unassignedCount})`, value: 'unassigned' },
            ]}
          />
        </div>

        {loading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : visible.length === 0 ? (
          <Empty description={scope === 'mine' ? 'No properties assigned to you' : 'No properties with actionable work'} />
        ) : (
          <List
            bordered
            style={{ background: '#fff' }}
            dataSource={visible}
            renderItem={(loc) => {
              const mine = me != null && loc.assigned_to === me;
              const oldestDays = loc.oldest_active ? dayjs().diff(dayjs(loc.oldest_active), 'day') : null;
              const meta = [
                loc.confirmed_count > 0 ? `${loc.confirmed_count} in progress` : null,
                oldestDays != null ? `oldest ${oldestDays}d` : null,
                loc.assigned_to && !mine ? `assigned ${dayjs(loc.assigned_at).fromNow()}` : null,
              ].filter(Boolean).join(' · ');
              return (
                <List.Item
                  actions={[
                    <Select
                      key="assign"
                      size="small"
                      style={{ width: 180 }}
                      placeholder="Assign…"
                      suffixIcon={<UserAddOutlined />}
                      value={loc.assigned_to ?? undefined}
                      loading={savingId === loc.location_id}
                      showSearch
                      optionFilterProp="label"
                      allowClear
                      onChange={(val) => assign(loc.location_id, (val as string) ?? null)}
                      onClear={() => assign(loc.location_id, null)}
                      options={rosterOptions}
                    />,
                    <Button key="open" type="primary" size="small" onClick={() => navigate(`/triage?loc=${encodeURIComponent(loc.location_id)}`)}>
                      Open queue <RightOutlined />
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={<HomeOutlined style={{ fontSize: 18, color: '#006EB7', marginTop: 4 }} />}
                    title={
                      <Space size={8} wrap>
                        <Text strong>{loc.location_name}</Text>
                        <Tag color="blue" style={{ margin: 0 }}>{loc.active_count} to triage</Tag>
                        {loc.p1_count > 0 && <Tag color="red" style={{ margin: 0 }}>{loc.p1_count} P1</Tag>}
                        {loc.p2_count > 0 && <Tag color="orange" style={{ margin: 0 }}>{loc.p2_count} P2</Tag>}
                        {mine
                          ? <Tag color="processing" style={{ margin: 0 }}>you</Tag>
                          : loc.assigned_to
                            ? <Tag style={{ margin: 0 }}>{loc.assigned_to_name || shortActor(loc.assigned_to)}</Tag>
                            : null}
                      </Space>
                    }
                    description={
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {meta || 'no open issues'}
                      </Text>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </div>
    </div>
  );
};

export default WorklistPage;
